import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so one failing component cannot blank the entire
 * application. Without this, any thrown error in any page unmounted the whole tree and
 * left the user on a white screen with no way to recover.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Replace with a real error-reporting service when one is configured.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          background: 'var(--bg-base)',
          color: 'var(--text-primary)',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: 20, margin: 0 }}>Something went wrong</h1>
        <p style={{ color: 'var(--text-secondary)', maxWidth: 420, margin: 0 }}>
          This part of the app failed to load. Reloading usually fixes it. If it keeps
          happening, please report it.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            padding: '10px 18px',
            borderRadius: 8,
            background: 'var(--brand-primary)',
            color: 'var(--text-inverse)',
            fontWeight: 600,
          }}
        >
          Reload
        </button>
        {process.env.NODE_ENV !== 'production' && (
          <pre
            style={{
              maxWidth: '90vw',
              overflowX: 'auto',
              textAlign: 'left',
              fontSize: 12,
              color: 'var(--text-muted)',
            }}
          >
            {error.message}
          </pre>
        )}
      </div>
    );
  }
}
